// OV2-7 / OV2-8 — GO TO THE CAULDRON, FROM ANYWHERE IN THE HOUSE. (Extracted by R26.)
//
// A member who has just sent something to the Cauldron should not have to find her own way back to
// the Magic Room and hunt for the pot. One helper, so the seal choice-list, the journal and anything
// added later all arrive the same way.
//
// R26 FOUND THAT SENTENCE WAS NOT TRUE, and how it stopped being true is the lesson. TEN surfaces
// send to the pot through the shared `SendToCauldronButton` — the journal, the spell page, oils,
// Moses, Greater Keys, herbs, the notepaper, the petition — and every one of them landed on
// "✓ In the Cauldron" and STOPPED. The way back was rendered at exactly ONE call site, the seal
// choice-list, which does not even use the shared sender. So the capability lived only on the one
// surface that needed it first, and the nine that shared a sender had none: W12's lesson exactly, a
// single hand-carved exception being why the rest of the room went without. The fix is structural —
// the trip lives in the shared sender now, so a surface added tomorrow arrives the same way by
// sending, not by remembering.
//
// THE CAULDRON IS NOT A ROUTE: it is a VIEW inside the Magic Room page, so a plain link cannot reach
// it. Two cases and both are covered on purpose:
//   · ALREADY on the Magic Room — the page is mounted, so the event finds its listener;
//   · ANYWHERE ELSE — the navigation remounts the page, and the event would fire BEFORE the listener
//     exists, so the request is ALSO left as a flag the page reads as its INITIAL view. The flag is
//     dropped shortly after, so a request that never landed cannot open the pot on some later visit.
//
// AND THE DOOR NAVIGATES THROUGH THE ROUTE MAP (R26-3). It used to hand-type BOTH halves of a mapping
// that already exists — `pushState({ page: "spell-book" }, "", "/magic-room")` beside
// `PAGE_PATHS["spell-book"] === "/magic-room"` in src/lib/pages.ts — which is R23/R24's shape: two
// literals that must agree, held by nothing. They agree today; the map is the single one now, and
// this is no longer the one navigation on the site that goes around it.
import { PAGE_PATHS } from "../lib/pages.js";
import { CLOSE_OVERLAYS_EVENT } from "./MobileNav.js";
import { t } from "../strings.js";
import { DEFAULT_LANGUAGE } from "../language.js";

export const OPEN_CAULDRON_EVENT = "twelve:open-cauldron";

// The page the pot lives inside. One name, resolved through the map to a path.
export const CAULDRON_PAGE = "spell-book";
export const cauldronPath = () => PAGE_PATHS[CAULDRON_PAGE];

// How long a request stays claimable. Long enough for the remount to read it, short enough that a
// request which never landed cannot open the pot on some later visit.
export const CAULDRON_REQUEST_MS = 2000;

let cauldronRequested = false;
export function consumeCauldronRequest() { const v = cauldronRequested; cauldronRequested = false; return v; }
export function cauldronRequestPending() { return cauldronRequested; }

export function goToCauldron() {
  cauldronRequested = true;
  // Whatever is up steps aside first, the same announcement the bottom bar makes (OV2-9).
  window.dispatchEvent(new CustomEvent(CLOSE_OVERLAYS_EVENT));
  const path = cauldronPath();
  if (window.location.pathname !== path) {
    window.history.pushState({ page: CAULDRON_PAGE }, "", path);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }
  window.dispatchEvent(new CustomEvent(OPEN_CAULDRON_EVENT));
  window.scrollTo({ top: 0, behavior: "smooth" });
  setTimeout(() => { cauldronRequested = false; }, CAULDRON_REQUEST_MS);
}

// Hung on `window` on purpose: it is the house's one door to the pot, and the rendered verification
// has to walk the REAL door rather than a re-creation of it (a harness that reimplements the thing it
// is checking proves only that the harness works).
if (typeof window !== "undefined") (window as unknown as Record<string, unknown>).__twelveGoToCauldron = goToCauldron;

// The button itself, so every surface that offers the trip offers the same one.
// 14-B7b: the label is a DEFAULT read from the catalog, not a required prop, because two gates call
// this with `{}` and read the English back (R26's own fixture, and ov2-78's). A caller's language
// wins; nobody's absence turns it into a blank button.
export function GoToCauldronButton({ className = "button button--primary", language = DEFAULT_LANGUAGE }: { className?: string; language?: string }) {
  return (
    <button type="button" className={className} onClick={goToCauldron}>
      {t(language, "cauldron.goTo")}
    </button>
  );
}
